import React, { useCallback, useState } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { DownloadOutlined as SystemUpdateAltIcon } from '@ant-design/icons';
import { Button } from 'antd';

import Alert from 'components/Alert';
import { saveAs } from 'file-saver';
import { formatDate } from 'helpers/date';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { BackgroundExportModal } from 'v2/components/Shared/BackgroundExportModal';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import leadsService from 'v2/services/leads/list';

const Export = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [totalItems] = useObservable(() => leadsService.getTotalItems(), []);

  const [exportReport] = useCallbackObservable(() => {
    setLoading(true);
    return leadsService.export().pipe(
      tap(leads => {
        const dateExport = formatDate(new Date(), 'dd-MM-yyyy');
        const fileExport = new Blob([leads as BlobPart], { type: 'text/csv; charset=utf-8' });
        saveAs(fileExport, `meus-leads-${dateExport}.csv`);
        setLoading(false);
      }),
      catchError(() => {
        setLoading(false);
        return of(true);
      })
    );
  }, []);

  const handleToggle = useCallback(async () => {
    if (totalItems > 50000) {
      await Alert.show({
        title: 'Atenção',
        message: 'É possível exportar no máximo 50 mil leads por relatório. Refaça o filtro para poder exportar.'
      });
      return;
    }

    if (totalItems > 3000) {
      setModalOpen(true);
      return;
    }

    exportReport();
  }, [totalItems, exportReport]);

  const onCloseBackgroundExport = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const onOKSubmit = useCallback(
    (email: string, callback: () => void) => leadsService.exportBackground(email).pipe(tap(callback)),
    []
  );

  return (
    <div>
      <PermissionHide action='myeduzz:meus-leads:exportar'>
        <Button
          id='leads-export-button'
          data-testid='leads-export-button'
          className='px-4 text-[#37474F] outline-hidden'
          disabled={!totalItems || loading}
          onClick={handleToggle}
          type='text'
          loading={loading}
          icon={<SystemUpdateAltIcon />}
        >
          Exportar
        </Button>
      </PermissionHide>
      <BackgroundExportModal
        reportDescription='leads'
        open={modalOpen}
        onClose={onCloseBackgroundExport}
        onOk={onOKSubmit}
      />
    </div>
  );
};

export default Export;
